import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * How long an add may stay unresolved before the button admits it is still
 * working.
 *
 * The checkmark burst fires on click, before the request — deliberately, because
 * on a warm connection the whole thing is over in under a second and optimism
 * reads as speed. But it is a claim of success, and when the modules that own
 * the drawer have not loaded yet that claim can stand for several seconds over a
 * cart UI that has not moved. Below this threshold the burst alone is right;
 * above it, it is a lie.
 */
const PENDING_STATE_DELAY = 450;

/**
 * Custom elements that consume `shopify:cart:lines-update`. Every one of them
 * subscribes in `connectedCallback`, so an add dispatched before their module
 * has run reaches nobody: the item lands in the cart server-side, the drawer
 * never opens, the cart icon never moves, and the next drawer open shows the
 * markup the page was served with. The customer sees an empty cart until they
 * reload.
 *
 * Their scripts are all `fetchpriority="low"`, which is right for page load and
 * wrong for a tap that beats them to it on a slow connection.
 */
const CART_UI_ELEMENTS = ['cart-items-component', 'cart-drawer-component', 'theme-drawer'];

/**
 * How long to wait for them.
 *
 * Generous, because waiting is no longer the same as staring at nothing: the
 * drawer is opened natively the moment the tap lands, so this is the customer
 * watching a skeleton fill in, not wondering whether anything happened. On Slow
 * 4G a dozen low-priority modules ahead of these in the queue can easily eat
 * four seconds.
 */
const CART_UI_READY_TIMEOUT = 10000;

/**
 * Deliberately NOT gated on: cart-icon and header-actions.
 *
 * header-actions only announces the count to screen readers and syncs
 * aria-expanded. cart-icon draws the bubble — worth getting right, but not worth
 * holding the drawer for, so rememberCartCount below feeds it through the
 * reconciliation path it already runs on connect.
 */

/**
 * Leaves the new cart count where a late-upgrading <cart-icon> will find it.
 *
 * CartIcon.ensureCartBubbleIsCorrect() already reads this key on connect and
 * repaints the bubble if it disagrees with the server-rendered markup, within a
 * ten second freshness window. Writing it here means an icon whose module lands
 * after the add still ends up correct, with no event and no extra machinery.
 *
 * @param {number} itemCount
 */
function rememberCartCount(itemCount) {
  // The bubble renders empty above 99, and CartIcon's own round-trip through
  // that state does not survive being read back. Leave those alone.
  if (!Number.isFinite(itemCount) || itemCount < 0 || itemCount >= 100) return;

  try {
    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(itemCount), timestamp: Date.now() }));
  } catch {
    // Private browsing — the bubble corrects itself on the next navigation.
  }
}

/**
 * Whether the cart drawer is open, and therefore whether the customer can see
 * that their add worked.
 */
function isCartDrawerOpen() {
  return document.getElementById('cart-drawer')?.hasAttribute('open') ?? false;
}

/**
 * The cart UI elements that are on the page but not yet upgraded.
 * @returns {string[]}
 */
function pendingCartUIElements() {
  return CART_UI_ELEMENTS.filter((tag) => !customElements.get(tag) && document.querySelector(tag));
}

/**
 * Resolves once every cart UI element present on the page has been upgraded, so
 * that dispatching the cart update actually reaches its listeners.
 *
 * On a warm page nothing is pending and this settles in a microtask, which keeps
 * the drawer's optimistic pre-open intact. It never delays the add-to-cart
 * request itself — that is already in flight by the time this is awaited.
 *
 * @param {number} [timeout]
 * @returns {Promise<boolean>} Whether the UI became ready.
 */
async function whenCartUIReady(timeout = CART_UI_READY_TIMEOUT) {
  const pending = pendingCartUIElements();
  if (pending.length === 0) return true;

  /** @type {number | undefined} */
  let timeoutId;
  const expired = new Promise((resolve) => {
    timeoutId = setTimeout(resolve, timeout);
  });

  try {
    await Promise.race([Promise.all(pending.map((tag) => customElements.whenDefined(tag))), expired]);
  } finally {
    clearTimeout(timeoutId);
  }

  return pendingCartUIElements().length === 0;
}

/** Mirrors ThemeDrawer's own MODAL_BREAKPOINT of 990. */
const DRAWER_MODAL_QUERY = '(max-width: 989px)';

/**
 * Opens the cart drawer without waiting for theme-drawer.js.
 *
 * The gap between the tap and the drawer appearing was that module downloading.
 * Nothing in the opening actually needs it: <dialog> opens natively, and every
 * visual — the slide-in, the panel, the backdrop — is plain CSS keyed off
 * [open], which is render-blocking and therefore already there. The module owns
 * closing, focus trapping and the squeeze, none of which matter in the first
 * second.
 *
 * ThemeDrawer.connectedCallback already handles arriving to a drawer that is
 * open — #onRestore() exists for the session-restore script in
 * theme-drawer.liquid, which opens drawers exactly this way. So when the module
 * lands it adopts this drawer and wires the close listeners and scroll lock,
 * with nothing to undo.
 *
 * @param {HTMLElement} drawer
 * @returns {boolean} Whether the drawer was opened.
 */
function openCartDrawerNatively(drawer) {
  const panel = /** @type {HTMLDialogElement | null} */ (drawer.querySelector('dialog'));
  if (!panel || panel.open) return false;

  // connectedCallback reads this back to seed its stack counter. The cart drawer
  // is the only thing that can be opening at this point, so it is first.
  drawer.style.setProperty('--drawer-stack-order', '1');

  panel.classList.add('theme-drawer__dialog--opening');
  panel.addEventListener('animationend', () => panel.classList.remove('theme-drawer__dialog--opening'), {
    once: true,
  });

  try {
    // Modal below the breakpoint, matching ThemeDrawer.open(). showModal() is
    // what puts it in the top layer and gives ::backdrop something to paint.
    if (window.matchMedia(DRAWER_MODAL_QUERY).matches) {
      panel.showModal();
    } else {
      panel.show();
    }
  } catch {
    return false;
  }

  drawer.setAttribute('open', '');

  // Escape closes a modal dialog natively, and that clears only the dialog's own
  // open state — <theme-drawer> would be left claiming to be open and would then
  // refuse to reopen. Keep the two in step until the module takes over.
  panel.addEventListener(
    'close',
    () => {
      drawer.removeAttribute('open');
      drawer.dispatchEvent(new CustomEvent('theme-drawer:close', { bubbles: true }));
    },
    { once: true }
  );

  // The squeeze and state-persistence listeners in theme-drawer.liquid are
  // plain document listeners, so they work without the component.
  drawer.dispatchEvent(new CustomEvent('theme-drawer:open', { bubbles: true }));

  return true;
}

/** Ceiling for a dispatcher that never reports back — see the listener below. */
const UNTRACKED_ADD_TIMEOUT = 8000;

/**
 * Opens the cart drawer into a placeholder state as soon as an add starts.
 *
 * This is the answer to the middle case: enough of the page has loaded that the
 * click is intercepted and the item really is being added, but the drawer's own
 * modules have not arrived, so nothing on screen moves. Setting the attribute
 * needs no JavaScript beyond this line — the busy markup is server-rendered and
 * the CSS is render-blocking — so the drawer shows the placeholder the moment it
 * opens, whenever that turns out to be.
 *
 * Opening still needs <theme-drawer>, so it is waited for rather than emulated:
 * open() owns modal vs squeeze mode, scroll lock, focus and the stack order, and
 * a hand-rolled copy would get one of them wrong. theme-drawer.js is small and
 * now modulepreloaded, so it lands well before the cart section does.
 *
 * @returns {HTMLElement | null} The drawer put into the busy state, if any.
 */
function beginCartDrawerBusy() {
  const drawer = document.getElementById('cart-drawer');

  // No drawer on the cart page, and nothing to open if the merchant has
  // auto-open switched off — in that case the header count is the feedback.
  if (!drawer || !drawer.querySelector('cart-drawer-component[auto-open]')) return null;

  drawer.dataset.cartUpdating = 'true';

  if (!drawer.hasAttribute('open')) {
    if (customElements.get('theme-drawer')) {
      // The real thing is here — use it. It handles focus restore, the stack
      // order and the squeeze properly.
      /** @type {any} */ (drawer).open?.();
    } else if (!openCartDrawerNatively(drawer)) {
      // Native open refused. Wait for the module rather than leaving the tap
      // with nothing to show for it.
      customElements.whenDefined('theme-drawer').then(() => {
        if (drawer.dataset.cartUpdating !== 'true' || drawer.hasAttribute('open')) return;

        /** @type {any} */ (drawer).open?.();
      });
    }
  }

  return drawer;
}

/**
 * Drops the placeholder once the real cart contents are in.
 *
 * Deferred two frames so the swap happens after the section morph has painted,
 * rather than exposing a frame of the pre-add cart underneath.
 *
 * @param {HTMLElement | null} drawer
 */
function endCartDrawerBusy(drawer) {
  if (!drawer) return;

  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      delete drawer.dataset.cartUpdating;
    })
  );
}

/**
 * Sends the customer to the cart page.
 *
 * The add already succeeded, so this is the same graceful degradation the theme
 * gets when the form submits before any JavaScript has run: no drawer, but a
 * correct cart and a route to checkout. Better than leaving them looking at a
 * page that shows no sign of what they just did.
 */
function fallbackToCartPage() {
  const cartUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart';

  if (window.location.pathname === cartUrl) {
    window.location.reload();
    return;
  }

  window.location.assign(cartUrl);
}

/**
 * Matches the cart payload emitted by `snippets/cart-items-component.liquid`.
 *
 * A string scan rather than DOMParser on purpose: this runs on the add-to-cart
 * critical path, and the alternative is parsing an entire re-rendered cart
 * section into a document just to read one node.
 */
const CART_STATE_PATTERN = /<script type="application\/json" data-cart-state>([\s\S]*?)<\/script>/;

/**
 * The section ids of every `cart-items-component` on the page. These are asked
 * for in the add-to-cart request so the response comes back carrying fully
 * rendered cart markup.
 *
 * @returns {string[]}
 */
function collectCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();

  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }

  return [...ids];
}

/**
 * Reads the cart out of the sections the add-to-cart response already returned.
 *
 * `/cart/add.js` answers with the added line, not the cart, so the theme used to
 * follow every add with a `/cart.json` round trip and hold the drawer closed
 * until it landed — roughly 700ms of dead time on mobile data, during which the
 * item was already in the cart server-side. The cart sections come back in the
 * same response, and they embed the cart JSON, so the round trip buys nothing.
 *
 * @param {Record<string, string> | undefined} sections
 * @returns {any | null} An AJAX-API-shaped cart, or null when no section carried one.
 */
export function readCartFromSections(sections) {
  for (const html of Object.values(sections ?? {})) {
    if (typeof html !== 'string') continue;

    const match = html.match(CART_STATE_PATTERN);
    if (!match?.[1]) continue;

    try {
      const cart = JSON.parse(match[1]);
      if (cart && Array.isArray(cart.items)) return cart;
    } catch {
      // Malformed payload — fall through to the network path rather than throw
      // away the add.
    }
  }

  return null;
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {number | undefined} */
  #pendingTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.endPending();
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Stops the button taking further clicks once an add has been unresolved for
   * PENDING_STATE_DELAY. A stalled add is exactly when a customer taps again,
   * and every extra tap is another line on the order.
   *
   * Deliberately not a visual state: the drawer's placeholder is the feedback,
   * and a second indicator on a button the drawer is covering adds nothing. The
   * one visual change is dropping `data-added` — the checkmark burst fires
   * optimistically on click, and past this threshold it is claiming a finished
   * add that has not finished.
   */
  beginPending() {
    clearTimeout(this.#pendingTimeout);

    this.#pendingTimeout = setTimeout(() => {
      const { addToCartButton } = this.refs;
      if (!addToCartButton) return;

      addToCartButton.removeAttribute('data-added');
      addToCartButton.dataset.pending = 'true';
      addToCartButton.setAttribute('aria-busy', 'true');
    }, PENDING_STATE_DELAY);
  }

  /** Clears the working state, whether or not it ever became visible. */
  endPending() {
    clearTimeout(this.#pendingTimeout);
    this.#pendingTimeout = undefined;

    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    addToCartButton.removeAttribute('data-pending');
    addToCartButton.removeAttribute('aria-busy');
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity - The quantity captured when Add was clicked.
 * @property {number} generation - The variant-change generation active when Add was clicked.
 * @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
 * @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
 * @property {string | null} variantResolutionUrl - A section-rendering URL that resolves the clicked selection.
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Adds queued while a variant change is in flight. Each entry captures the selection state and
   * generation active when Add was clicked, then resolves that selection at drain time.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * The in-flight variant-change section fetch promise. The queue drain awaits this before
   * reading the resolved variant id.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    // One `sections` field, not one per component: the append used to sit inside
    // the loop, so a page with two cart components sent the list twice and a page
    // with none sent nothing at all — which is what forced the /cart.json
    // fallback below.
    const cartItemComponentsSectionIds = collectCartSectionIds();
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    // Dispatch only once the listeners exist — see whenCartUIReady. Resolving
    // the promise later is safe either way: a listener that attaches `.then` to
    // an already-resolved promise still runs.
    const cartUIReady = whenCartUIReady();
    const cartUpdateDispatched = cartUIReady.then(() => {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [
            {
              merchandiseId: /** @type {string} */ (formData.get('id')),
              quantity: itemCount,
            },
          ],
          promise: deferredEventPromise.promise,
        })
      );
    });

    for (const container of allAddToCartContainers) container.beginPending?.();
    const busyDrawer = beginCartDrawerBusy();

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          await cartUpdateDispatched;

          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // The dispatch is gated on the cart UI being upgraded, so wait for it
          // before resolving — otherwise the resolution can outrun the event and
          // the whole update is delivered to an empty room.
          await cartUpdateDispatched;

          // Leave for the cart page ONLY if there is still nothing to look at.
          //
          // The previous rule — any ungated element still pending — sent people
          // to /cart after the drawer had already opened and shown them the
          // item, which is the worst of both: they waited, it worked, and then
          // the page navigated out from under them anyway. An open drawer means
          // the add is visible, so stay put and let it fill in.
          if (!isCartDrawerOpen() && pendingCartUIElements().length > 0) {
            fallbackToCartPage();
            return;
          }

          // Fast path: the response already carries the re-rendered cart sections,
          // and those sections embed the cart JSON. Everything downstream — the
          // drawer morph, the auto-open, the cart icon, the pairing prompt — is
          // gated on this promise, so resolving it here paints the drawer as soon
          // as the add lands instead of a round trip later.
          const cartFromSections = readCartFromSections(response.sections);

          if (cartFromSections) {
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartFromSections),
              detail: {
                items: cartFromSections.items,
                source: 'product-form-component',
                sourceId: this.id.toString(),
                itemCount,
                productId: this.dataset.productId,
                sections: response.sections,
                didError: false,
              },
            });

            rememberCartCount(cartFromSections.item_count);

            if (this.#getVariantIdInput()) this.#updateCartQuantity(cartFromSections);

            return;
          }

          // Fallback: no cart section came back — cart type "page" on a product
          // template, or a section rendered before the payload existed.
          await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              rememberCartCount(ajaxCart.item_count);

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }

              return ajaxCart;
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        // Runs after `await cartUpdateDispatched`, so the placeholder clears when
        // the cart UI has actually received the update — not when the request
        // landed.
        for (const container of allAddToCartContainers) container.endPending?.();
        endCartDrawerBusy(busyDrawer);

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemComponentsSectionIds = collectCartSectionIds();

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    // See #processAddToCart — the dispatch waits for its listeners to exist.
    const cartUIReady = whenCartUIReady();
    const cartUpdateDispatched = cartUIReady.then(() => {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({
            merchandiseId: item.variantId,
            quantity: item.quantity,
          })),
          promise: deferredEventPromise.promise,
        })
      );
    });

    const batchAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    for (const container of batchAddToCartContainers) container.beginPending?.();
    const busyDrawer = beginCartDrawerBusy();

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
      sections_url: window.location.pathname,
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          await cartUpdateDispatched;

          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await cartUpdateDispatched;

        // Same fast path as the single add — see readCartFromSections.
        const cart = readCartFromSections(response.sections) ?? (await this.#refreshCart());

        // See #processAddToCart.
        if (!isCartDrawerOpen() && pendingCartUIElements().length > 0) {
          fallbackToCartPage();
          return;
        }

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        for (const container of batchAddToCartContainers) container.endPending?.();
        endCartDrawerBusy(busyDrawer);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    // Hold the in-flight section-fetch promise so the queue drain can await it before reading the
    // resolved variant id.
    this.#pendingVariantChange = event.promise;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        await this.#drainAddToCartQueue();
      }
    }
  };

  /**
   * Drains the add-to-cart queue accumulated while a variant change was in flight.
   *
   * Each queued add resolves against the selection and generation that were active when Add was
   * clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
   * variant id is never sent. The add-to-cart button is already disabled for unavailable
   * selections in #onProductSelect, so no further UI change is needed.
   */
  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];

    /** @type {Array<{variantId: string, quantity: number}>} */
    const resolvedItems = [];
    for (const item of queuedItems) {
      const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
      if (resolvedItem) {
        resolvedItems.push(resolvedItem);
      }
    }

    this.#processBatchAddToCart(resolvedItems);
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number} | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) return null;

    return {
      variantId,
      quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints),
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;
    /** @type {boolean | undefined} */
    let available;
    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await item.pendingVariantChange
        );
        const resource = result?.detail?.resource;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch {
        const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
        }));
        resolvedVariantId = resolvedVariant.variantId;
        available = resolvedVariant.available;
        quantityConstraints = resolvedVariant.quantityConstraints;
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return { variantId, quantityConstraints };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : Math.max(max - cartQuantity, min);

    let normalizedQuantity = quantity;
    if ((quantity - min) % step !== 0) {
      normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
    }

    return Math.max(min, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * Resolves a queued selection using the server-side section renderer when the original in-flight
   * request was aborted by a later variant selection.
   * @param {string | null} variantResolutionUrl
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };

    const response = await fetch(variantResolutionUrl, { credentials: 'same-origin' });
    if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) return { variantId: null, available: false, quantityConstraints };

    const resource = JSON.parse(textContent);
    if (!resource || typeof resource !== 'object') return { variantId: null, available: false, quantityConstraints };

    return {
      variantId: resource.id != null ? String(resource.id) : null,
      available: resource.available !== false,
      quantityConstraints,
    };
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  /**
   * Whether the current selection's add-to-cart button is disabled (unavailable selection).
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

/**
 * Arms the drawer placeholder for adds this module did not make.
 *
 * The wishlist popover runs its own fetch and never touches
 * #processAddToCart — so did every other bespoke add path, and none of them
 * armed the placeholder. Their adds sat with no feedback at all until the
 * drawer opened on its own.
 *
 * Rather than patch each one, hook the thing they already have in common: every
 * add-to-cart path in the theme announces itself with the standard cart event
 * before it starts work. Listening here covers all of them, including any app
 * or block that dispatches it, and any path that does not exist yet.
 *
 * #processAddToCart still arms the placeholder directly rather than relying on
 * this, because its own dispatch is deliberately held until the drawer's
 * modules exist — which is exactly the wait the placeholder is there to cover.
 * Both calls landing is harmless: the second is a no-op on an open drawer.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const cartEvent = /** @type {CartLinesUpdateEvent} */ (/** @type {unknown} */ (event));
  if (cartEvent.action !== 'add') return;

  // Adds from inside a modal — quick add — carry their own feedback, and
  // cart-drawer.js deliberately defers the drawer until that modal closes so
  // focus restoration runs first. Opening over the top of one would fight that.
  if (event.target instanceof Element && event.target.closest('dialog:modal')) return;

  const drawer = beginCartDrawerBusy();
  if (!drawer) return;

  const { promise } = cartEvent;

  // A dispatcher that attached no promise never tells us it finished, so give
  // the placeholder a ceiling rather than leaving it up for the session.
  if (!promise) {
    setTimeout(() => endCartDrawerBusy(drawer), UNTRACKED_ADD_TIMEOUT);
    return;
  }

  const done = () => endCartDrawerBusy(drawer);
  promise.then(done, done);
});
